import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithRedirect
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { UserService } from '../services/UserService';

// Initialize Google provider with custom parameters
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('profile');
googleProvider.addScope('email');
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

export const signInWithGoogle = async () => {
  try {
    await signInWithRedirect(auth, googleProvider);
  } catch (error: any) {
    console.error('Google sign in error:', error);
    if (error.code === 'auth/operation-not-allowed') {
      throw new Error('O login com Google não está habilitado. Por favor, contate o administrador.');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error(
        'O navegador bloqueou a janela de login. Para continuar:\n' +
        '1. Procure o ícone de bloqueio de popup na barra de endereço\n' +
        '2. Clique nele e selecione "Sempre permitir popups deste site"\n' +
        '3. Tente fazer login novamente'
      );
    } else if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('A operação foi cancelada pelo usuário.');
    }
    throw error;
  }
};

export const registerWithEmail = async (
  email: string,
  password: string,
  name: string,
  role: 'patient' | 'doctor' | 'fisioterapeuta' | 'interno' | 'financeiro',
  clientId?: string // O clientId agora é opcional
): Promise<FirebaseUser> => {
  try {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    
    await updateProfile(user, { displayName: name });

    await UserService.createUser(
        {
            firebase_uid: user.uid,
            userEmail: email,
            userDisplayName: name,
        },
        role,
        clientId
    );

    return user;

  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('Este email já está em uso');
    }
    throw error;
  }
};

export const loginWithEmail = async (
  email: string,
  password: string
): Promise<FirebaseUser> => {
  try {
    // First authenticate the user
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    
    // Then fetch the user's data from Firestore (handled by AuthContext now)
    // No need to create user document here, AuthContext will handle it
    
    return user;
  } catch (error: any) {
    console.error('Login error:', error);
    
    // Handle specific authentication errors
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error('Email ou senha incorretos');
    } else if (error.code === 'auth/invalid-credential') {
      throw new Error('Credenciais inválidas');
    } else if (error.code === 'auth/too-many-requests') {
      throw new Error('Muitas tentativas de login. Por favor, tente novamente mais tarde.');
    }
    
    // If it's a custom error we created, pass it through
    if (error.message.includes('permissão') || error.message.includes('perfil atribuído')) {
      throw error;
    }
    
    // For any other errors, throw a generic message
    throw new Error('Erro ao fazer login. Por favor, tente novamente ou contate o suporte.');
  }
};

export const logout = async (): Promise<void> => {
  await signOut(auth);
};

export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    if (error.code === 'auth/user-not-found') {
      throw new Error('Não existe uma conta com este email');
    }
    throw error;
  }
};
